/**
 * Voorziene kostenplaatsen ("Provisional units") section.
 *
 * Lets the user pick which units a project part will impact *before* any
 * budget is assigned, and flag whether each unit must be empty to execute
 * the work. The estimated rental loss during the locked project window is
 * computed from `unit.estimatedRentalValue` × project duration in days /
 * 30.43 (avg days per month).
 */

import React, { useEffect, useMemo, useState } from "react";
import { useSession } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { toast } from "react-toastify";
import {
  Cube,
  Plus,
  Trash,
  CurrencyEur,
  Info,
  Warning,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  ProjectPart,
  ProjectPartProvisionalUnit,
} from "shared/types/renovation";
import { UnitDTO } from "shared/types/unit";

const AVG_DAYS_PER_MONTH = 30.4375; // accurate enough for huurderving math

interface ProvisionalUnitsSectionProps {
  part: ProjectPart;
  buildingId?: string;
  /** Locked planning days — the duration used to compute huurderving. */
  projectDurationInDays?: number | null;
  /** True when the project planning is locked so we can label the
   *  estimate as based on the approved planning. */
  isPlanningLocked?: boolean;
  onUpdate: () => void;
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("nl-BE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);

export const ProvisionalUnitsSection: React.FC<ProvisionalUnitsSectionProps> = ({
  part,
  buildingId,
  projectDurationInDays,
  isPlanningLocked,
  onUpdate,
}) => {
  const { session } = useSession();
  const provisional: ProjectPartProvisionalUnit[] =
    part.provisionalUnits ?? [];

  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [buildingUnits, setBuildingUnits] = useState<UnitDTO[]>([]);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load units the moment the picker opens. Cheap fetch, scoped to building.
  useEffect(() => {
    if (!pickerOpen || !buildingId || !session) return;
    let cancelled = false;
    setLoadingUnits(true);
    fetchWithAuth(
      `${API_URL}/units/building/${buildingId}`,
      { method: "GET" },
      session,
    )
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : data.data || [];
        setBuildingUnits(list);
      })
      .catch((err) => console.error("Error loading units:", err))
      .finally(() => {
        if (!cancelled) setLoadingUnits(false);
      });
    return () => {
      cancelled = true;
    };
  }, [pickerOpen, buildingId, session]);

  // Persist any change. The endpoint takes the full desired set so we send
  // the current array as the source of truth.
  const persist = async (
    next: { unitId: string; mustBeEmpty: boolean }[],
  ) => {
    setIsSubmitting(true);
    try {
      const res = await fetchWithAuth(
        `${API_URL}/renovation/project/part/${part.id}/provisional-units`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ units: next }),
        },
        session,
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Failed to update provisional units");
      }
      onUpdate();
    } catch (err) {
      console.error("Error updating provisional units:", err);
      toast.error(
        err instanceof Error
          ? err.message
          : "Fout bij bijwerken voorziene kostenplaatsen",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const toCurrent = () =>
    provisional.map((p) => ({
      unitId: p.unit.id,
      mustBeEmpty: !!p.mustBeEmpty,
    }));

  const handleAddUnit = async (unit: UnitDTO) => {
    if (!unit.id) return;
    if (provisional.some((p) => p.unit.id === unit.id)) return;
    await persist([
      ...toCurrent(),
      { unitId: unit.id, mustBeEmpty: false },
    ]);
  };

  const handleRemove = async (unitId: string) => {
    await persist(toCurrent().filter((u) => u.unitId !== unitId));
  };

  const handleToggleMustBeEmpty = async (unitId: string, value: boolean) => {
    await persist(
      toCurrent().map((u) =>
        u.unitId === unitId ? { ...u, mustBeEmpty: value } : u,
      ),
    );
  };

  // Estimated rental loss for the locked project duration. Only units
  // flagged as "must be empty" contribute. Falls back to "—" if there is
  // no locked duration yet.
  const huurderving = useMemo(() => {
    if (!projectDurationInDays || projectDurationInDays <= 0) return null;
    const months = projectDurationInDays / AVG_DAYS_PER_MONTH;
    let total = 0;
    let countContributing = 0;
    for (const link of provisional) {
      if (!link.mustBeEmpty) continue;
      const monthly = Number(link.unit?.estimatedRentalValue || 0);
      if (monthly <= 0) continue;
      total += monthly * months;
      countContributing += 1;
    }
    return { total, months, countContributing };
  }, [provisional, projectDurationInDays]);

  const filteredUnits = useMemo(() => {
    const term = search.trim().toLowerCase();
    const withIds = buildingUnits.filter(
      (u): u is UnitDTO & { id: string } => !!u.id,
    );
    if (!term) return withIds;
    return withIds.filter((u) => u.id.toLowerCase().includes(term));
  }, [buildingUnits, search]);

  return (
    <TooltipProvider>
      <div className="rounded-lg border border-neutral-200 bg-white p-4">
        <div className="mb-3 flex items-center justify-between border-b border-neutral-100 pb-2">
          <div className="flex items-center gap-2">
            <Cube
              size={16}
              weight="duotone"
              className="text-moss-primary"
            />
            <h4 className="text-sm font-medium text-neutral-700">
              Voorziene kostenplaatsen
            </h4>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info
                  size={13}
                  className="cursor-help text-neutral-400"
                />
              </TooltipTrigger>
              <TooltipContent>
                <p className="max-w-xs text-xs">
                  Markeer de units die door dit projectonderdeel geraakt
                  worden, nog vóór er budget aan gekoppeld is. Vink "moet
                  leeg" aan om de geschatte huurderving mee te nemen.
                </p>
              </TooltipContent>
            </Tooltip>
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setPickerOpen(true)}
            disabled={!buildingId || isSubmitting}
            className="border-moss-primary/30 text-moss-primary hover:bg-moss-primary/10"
          >
            <Plus size={14} className="mr-1" />
            Kostenplaats toevoegen
          </Button>
        </div>

        {provisional.length === 0 ? (
          <div className="rounded-md border border-dashed border-neutral-300 bg-neutral-50 p-4 text-center">
            <p className="text-sm text-neutral-600">
              Nog geen voorziene kostenplaatsen
            </p>
            <p className="mt-0.5 text-xs text-neutral-400">
              Voeg de units toe waarop dit onderdeel van toepassing is.
            </p>
          </div>
        ) : (
          <ul className="space-y-2">
            {provisional.map((link) => {
              const monthly = Number(link.unit?.estimatedRentalValue || 0);
              return (
                <li
                  key={link.id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-neutral-200 bg-neutral-50/40 px-3 py-2"
                >
                  <div className="flex items-center gap-2">
                    <Cube
                      size={14}
                      weight="duotone"
                      className="text-moss-primary"
                    />
                    <span className="text-neutral-primary text-sm font-medium">
                      {link.unit.id}
                    </span>
                    {monthly > 0 && (
                      <span className="text-neutral-secondary-mid text-xs">
                        · {formatCurrency(monthly)}/maand
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 text-xs text-neutral-600">
                      <Switch
                        checked={!!link.mustBeEmpty}
                        onCheckedChange={(v) =>
                          handleToggleMustBeEmpty(link.unit.id, v)
                        }
                        disabled={isSubmitting}
                      />
                      <span>Moet leeg zijn</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => handleRemove(link.unit.id)}
                      disabled={isSubmitting}
                      className="rounded-md p-1 text-neutral-400 transition-colors hover:bg-rose-100 hover:text-rose-600"
                      title="Verwijderen"
                    >
                      <Trash size={14} />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {/* Geschatte huurderving — only meaningful with a locked planning. */}
        <div className="mt-3 rounded-md border border-neutral-200 bg-neutral-50 p-3 text-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CurrencyEur
                size={14}
                weight="duotone"
                className="text-moss-primary"
              />
              <span className="font-medium text-neutral-700">
                Geschatte huurderving tijdens looptijd project
              </span>
            </div>
            {huurderving ? (
              <span className="text-moss-primary text-base font-semibold tabular-nums">
                {formatCurrency(huurderving.total)}
              </span>
            ) : (
              <span className="text-neutral-secondary-mid text-xs italic">
                Vergrendel eerst de planning
              </span>
            )}
          </div>
          {huurderving && (
            <p className="text-neutral-secondary-mid mt-1 flex items-center gap-1.5 text-xs">
              {!isPlanningLocked && (
                <Warning
                  size={12}
                  weight="fill"
                  className="text-amber-500"
                />
              )}
              {projectDurationInDays} dagen ≈{" "}
              {huurderving.months.toFixed(1)} maanden ·{" "}
              {huurderving.countContributing} unit(s) "moet leeg"
              {!isPlanningLocked && " · planning nog niet vergrendeld"}
            </p>
          )}
        </div>

        {/* Unit picker dialog */}
        <Dialog
          open={pickerOpen}
          onOpenChange={(open) => {
            setPickerOpen(open);
            if (!open) setSearch("");
          }}
        >
          <DialogContent className="max-h-[80vh] overflow-hidden sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Kostenplaats toevoegen</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Zoek op unit ID..."
              />
              <div className="max-h-[50vh] space-y-1 overflow-y-auto">
                {loadingUnits ? (
                  <p className="text-neutral-secondary-mid py-6 text-center text-sm">
                    Units laden…
                  </p>
                ) : filteredUnits.length === 0 ? (
                  <p className="text-neutral-secondary-mid py-6 text-center text-sm">
                    Geen units gevonden
                  </p>
                ) : (
                  filteredUnits.map((unit) => {
                    const alreadyAdded = provisional.some(
                      (p) => p.unit.id === unit.id,
                    );
                    return (
                      <button
                        key={unit.id}
                        type="button"
                        disabled={alreadyAdded || isSubmitting}
                        onClick={async () => {
                          await handleAddUnit(unit);
                          setPickerOpen(false);
                          setSearch("");
                        }}
                        className={`flex w-full items-center justify-between rounded-md border px-3 py-2 text-left text-sm transition-colors ${
                          alreadyAdded
                            ? "cursor-not-allowed border-neutral-200 bg-neutral-50 text-neutral-400"
                            : "hover:border-moss-primary/40 hover:bg-moss-primary/5 border-neutral-200"
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          <Cube
                            size={14}
                            weight="duotone"
                            className={
                              alreadyAdded
                                ? "text-neutral-300"
                                : "text-moss-primary"
                            }
                          />
                          <span className="font-medium">{unit.id}</span>
                          {Number(unit.estimatedRentalValue || 0) > 0 && (
                            <span className="text-xs text-neutral-500">
                              · {formatCurrency(Number(unit.estimatedRentalValue))}/maand
                            </span>
                          )}
                        </span>
                        {alreadyAdded && (
                          <span className="text-[10px] uppercase tracking-wide text-neutral-400">
                            Toegevoegd
                          </span>
                        )}
                      </button>
                    );
                  })
                )}
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setPickerOpen(false)}
              >
                Sluiten
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
};

export default ProvisionalUnitsSection;
